import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrPresenceAvatar from "./GrPresenceAvatar";
import GrCountBadge from "./GrCountBadge";
import { gr } from "../tokens";

const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Rows next to each other get a hairline between them (the outer element carries it, with or
// without an action beside the row).
const ROW_CLASS = "gr-chat-row";
// Room kept on the right of a row with an action (a short text button such as "Group Detail").
const ACTION_SPACE = `calc(${gr.space[12]} * 2 + ${gr.space[4]})`;
const rowBorderSx = {
  [`.${ROW_CLASS} + &`]: { borderTop: `1px solid ${gr.color.line2}` },
};

// Display name without the literal "undefined" / "null" words some records carry.
export const grCleanName = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter((part) => part && !/^(undefined|null)$/i.test(part))
    .join(" ");

// Conversation / person row (chat inbox, New Message list): avatar with online dot, name,
// one-line preview, time on the right, unread count. Unread rows sit on tint, the open one on brand-soft.
// `action` (e.g. a "Group Detail" link on the chosen group) sits at the right edge beside the row, not inside it,
// so the row button and the action button are never nested.
export default function GrChatListRow({
  src,
  name,
  online = false,
  preview,
  time,
  tag,
  unread = 0,
  selected = false,
  onClick,
  action,
  sx,
}) {
  const isUnread = Number(unread) > 0;
  const display = grCleanName(name);
  const handleKeyDown = (e) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick(e);
    }
  };
  const row = (
    <Box
      className={action ? undefined : ROW_CLASS}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-current={selected || undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: gr.space[3],
        px: gr.space[4],
        py: gr.space[3],
        cursor: onClick ? "pointer" : "default",
        fontFamily: gr.font.family,
        background: selected
          ? gr.color.brandSoft
          : isUnread
          ? gr.color.tint
          : gr.color.surface,
        boxShadow: selected ? `inset 3px 0 0 ${gr.color.brand}` : "none",
        transition: "background-color .15s ease",
        ...(action ? { pr: ACTION_SPACE } : rowBorderSx),
        "&:hover": {
          background: selected || isUnread ? gr.color.brandSoft : gr.color.tint,
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `inset 0 0 0 2px ${gr.color.brand}`,
        },
        ...sx,
      }}
    >
      <GrPresenceAvatar src={src} name={display} online={online} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: gr.space[2],
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              ...ellipsis,
              flex: 1,
              minWidth: 0,
              fontSize: gr.font.size.md,
              fontWeight: gr.font.weight.semibold,
              lineHeight: gr.font.lineHeight.normal,
              color: gr.color.ink,
              textTransform: "capitalize",
            }}
          >
            {display}
          </Box>
          {time ? (
            <Box
              component="span"
              sx={{
                flex: "none",
                fontSize: gr.font.size.xs,
                color: isUnread ? gr.color.brandDark : gr.color.muted,
                fontWeight: isUnread
                  ? gr.font.weight.semibold
                  : gr.font.weight.regular,
              }}
            >
              {time}
            </Box>
          ) : null}
        </Box>
        {preview || tag || isUnread ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: gr.space[2],
              minWidth: 0,
            }}
          >
            <Box
              component="span"
              sx={{
                ...ellipsis,
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: gr.space[1],
                fontSize: gr.font.size.sm,
                lineHeight: gr.font.lineHeight.normal,
                color: isUnread ? gr.color.ink : gr.color.muted,
                fontWeight: isUnread
                  ? gr.font.weight.medium
                  : gr.font.weight.regular,
                "& > svg": { flex: "none" },
              }}
            >
              {tag}
              {preview ? (
                <Box component="span" sx={{ ...ellipsis, minWidth: 0 }}>
                  {preview}
                </Box>
              ) : null}
            </Box>
            <GrCountBadge
              count={unread}
              tone="solid"
              label={`${unread} unread`}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
  if (!action) return row;
  return (
    <Box className={ROW_CLASS} sx={{ position: "relative", ...rowBorderSx }}>
      {row}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: gr.space[2],
          transform: "translateY(-50%)",
        }}
      >
        {action}
      </Box>
    </Box>
  );
}

GrChatListRow.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  online: PropTypes.bool,
  preview: PropTypes.node,
  time: PropTypes.node,
  tag: PropTypes.node,
  unread: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  action: PropTypes.node,
  sx: PropTypes.object,
};
